import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

export default function OrderExchangeManagement({ orderIdProp, inModal = false, onSuccess }: { orderIdProp?: string, inModal?: boolean, onSuccess?: () => void }) {
    const params = useParams();
    const id = orderIdProp || params.id;
    const toast = useToast();
    const [order, setOrder] = useState<any>(null);
    const [exchanges, setExchanges] = useState<ExchangeRecord[]>([]);
    const [exchangeDealers, setExchangeDealers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingExchange, setEditingExchange] = useState<ExchangeRecord | null>(null);
    const [formData, setFormData] = useState({
        invoice_item_id: '',
        exchange_dealer_id: '',
        old_product_model: '',
        old_product_brand: '',
        old_registration_number: '',
        old_product_age_years: '',
        old_product_condition: 'good',
        exchange_value: '',
        exchange_remarks: ''
    });

    useEffect(() => {
        loadData();
    }, [id]);

    // Handle direct add for modal view
    useEffect(() => {
        if (inModal && exchanges.length === 0 && !showForm && !editingExchange) {
            handleAdd();
        }
    }, [inModal, exchanges.length, showForm, editingExchange]);

    const loadData = async () => {
        try {
            const [orderRes, exchangesRes, dealersRes] = await Promise.all([
                api.get(`/orders/${id}`),
                api.get(`/orders/${id}/exchanges`),
                api.get('/exchange_dealers')
            ]);

            // Get order data
            const orderData = orderRes.data;

            // Get invoice data from exchanges response
            if (exchangesRes.invoice) {
                orderData.invoice = exchangesRes.invoice;
            }

            setOrder(orderData);
            const exchangesData = Array.isArray(exchangesRes.data) ? exchangesRes.data : [];
            setExchanges(exchangesData);
            setExchangeDealers(dealersRes.data.data || dealersRes.data || []);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load exchange data');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingExchange(null);
        setFormData({
            invoice_item_id: '',
            exchange_dealer_id: '',
            old_product_model: '',
            old_product_brand: '',
            old_registration_number: '',
            old_product_age_years: '',
            old_product_condition: 'good',
            exchange_value: '',
            exchange_remarks: ''
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            let response;
            if (editingExchange) {
                response = await api.put(`/orders/${id}/exchanges/${editingExchange.id}`, formData);
            } else {
                response = await api.post(`/orders/${id}/exchanges`, formData);
            }

            if (response.success) {
                toast.success(`Exchange record ${editingExchange ? 'updated' : 'created'} successfully`);
                loadData();
                resetForm();
                if (onSuccess) onSuccess();
            } else {
                const msg = response.errors?.length > 0 ? response.errors[0] : (response.message || response.error || 'Error saving exchange record');
                toast.error(msg);
            }
        } catch (error: any) {
            console.error('Error saving exchange:', error);
            toast.error(error.message || 'An unexpected error occurred');
        }
    };

    const handleEdit = (exchange: ExchangeRecord) => {
        setEditingExchange(exchange);
        setFormData({
            invoice_item_id: exchange.invoice_item_id,
            exchange_dealer_id: exchange.exchange_dealer_id || '',
            old_product_model: exchange.old_product_model || '',
            old_product_brand: exchange.old_product_brand || '',
            old_registration_number: '',
            old_product_age_years: '',
            old_product_condition: exchange.old_product_condition || 'good',
            exchange_value: exchange.exchange_value?.toString() || '',
            exchange_remarks: ''
        });
        setShowForm(true);
    };

    const handleAdd = () => {
        setEditingExchange(null);
        setFormData({
            invoice_item_id: order?.invoice?.invoice_items?.length === 1 ? order.invoice.invoice_items[0].id : '',
            exchange_dealer_id: '',
            old_product_model: '',
            old_product_brand: '',
            old_registration_number: '',
            old_product_age_years: '',
            old_product_condition: 'good',
            exchange_value: '',
            exchange_remarks: ''
        });
        setShowForm(true);
    };

    const handleDelete = async (exchangeId: string) => {
        if (!confirm('Are you sure you want to delete this exchange record?')) return;
        try {
            await api.delete(`/orders/${id}/exchanges/${exchangeId}`);
            loadData();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error deleting exchange:', error);
        }
    };


    const getConditionBadge = (condition: string) => {
        const badges: Record<string, string> = {
            excellent: 'bg-green-100 text-green-700',
            good: 'bg-blue-100 text-blue-700',
            fair: 'bg-yellow-100 text-yellow-700',
            poor: 'bg-red-100 text-red-700'
        };
        return badges[condition] || badges.good;
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (!order) return <div className="p-8">Order not found.</div>;

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <div className="mb-8">
                {!inModal && (
                    <Link to={`/orders/${id}`} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-bold mb-4">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Order
                    </Link>
                )}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className={`${inModal ? 'text-2xl' : 'text-3xl'} font-black text-slate-900`}>Exchange Management</h1>
                        {!inModal && <p className="text-slate-600 mt-2">Order #{order.order_number}</p>}
                    </div>
                    <button
                        onClick={handleAdd}
                        className="px-6 py-3 bg-orange-600 text-white rounded-xl font-bold hover:bg-orange-700 transition-all text-sm"
                    >
                        + Add Exchange
                    </button>
                </div>
            </div>

            {/* Exchange Records Table */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Product</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Old Product</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Condition</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">Exchange Value</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {exchanges.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                    No exchange records found. Click "+ Add Exchange" to create one.
                                </td>
                            </tr>
                        ) : (
                            exchanges.map((ex) => (
                                <tr key={ex.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4 font-medium text-slate-900">{ex.product_name || 'Unknown'}</td>
                                    <td className="px-6 py-4 text-slate-700">
                                        {ex.old_product_brand} {ex.old_product_model}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getConditionBadge(ex.old_product_condition || 'good')}`}>
                                            {ex.old_product_condition || 'good'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right font-mono text-slate-900">
                                        {ex.exchange_value ? `₹${ex.exchange_value.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={() => handleEdit(ex)}
                                            className="text-indigo-600 hover:text-indigo-800 font-bold mr-4"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(ex.id)}
                                            className="text-red-600 hover:text-red-800 font-bold"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Form Section */}
            {showForm && (
                <div className={inModal ? "mb-8 bg-slate-50 rounded-2xl p-8 border border-slate-200" : "fixed inset-0 bg-black/50 flex items-center justify-center z-50"}>
                    <div className={inModal ? "" : "bg-white rounded-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
                        <h2 className="text-2xl font-black text-slate-900 mb-6">
                            {editingExchange ? 'Edit Exchange' : 'Add Exchange'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {!editingExchange && (
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Product</label>
                                    {!order.invoice || !order.invoice.invoice_items || order.invoice.invoice_items.length === 0 ? (
                                        <div className="w-full px-4 py-3 border-2 border-amber-300 bg-amber-50 rounded-xl text-amber-800">
                                            <div className="flex items-center gap-2">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <span className="font-bold">This order doesn't have an invoice yet. Create an invoice first to add exchanges.</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <select
                                            required
                                            value={formData.invoice_item_id}
                                            onChange={(e) => setFormData({ ...formData, invoice_item_id: e.target.value })}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        >
                                            <option value="">Select Product</option>
                                            {order.invoice.invoice_items?.map((item: any) => (
                                                <option key={item.id} value={item.id}>
                                                    {item.product_name}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Exchange Dealer</label>
                                <select
                                    value={formData.exchange_dealer_id}
                                    onChange={(e) => setFormData({ ...formData, exchange_dealer_id: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                >
                                    <option value="">Select Dealer</option>
                                    {exchangeDealers.map((dealer) => (
                                        <option key={dealer.id} value={dealer.id}>{dealer.dealer_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Old Product Brand</label>
                                    <input
                                        type="text"
                                        value={formData.old_product_brand}
                                        onChange={(e) => setFormData({ ...formData, old_product_brand: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="Honda"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Old Product Model</label>
                                    <input
                                        type="text"
                                        value={formData.old_product_model}
                                        onChange={(e) => setFormData({ ...formData, old_product_model: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="Activa 5G"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Condition</label>
                                    <select
                                        value={formData.old_product_condition}
                                        onChange={(e) => setFormData({ ...formData, old_product_condition: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    >
                                        <option value="excellent">Excellent</option>
                                        <option value="good">Good</option>
                                        <option value="fair">Fair</option>
                                        <option value="poor">Poor</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Exchange Value</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.exchange_value}
                                        onChange={(e) => setFormData({ ...formData, exchange_value: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Remarks</label>
                                <textarea
                                    rows={3}
                                    value={formData.exchange_remarks}
                                    onChange={(e) => setFormData({ ...formData, exchange_remarks: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    placeholder="Additional notes..."
                                />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    type="submit"
                                    disabled={!editingExchange && (!order.invoice || !order.invoice.invoice_items || order.invoice.invoice_items.length === 0)}
                                    className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-xl font-bold hover:bg-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {editingExchange ? 'Update' : 'Create'} Exchange Record
                                </button>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-6 py-3 bg-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-300 transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
